/**
 * Pure helpers for the Sales overview trend chart and movers (no React; unit-testable in node).
 *
 *   bucketTrend(trend, { weekly })  -> [{ key, from, to, previous_from, previous_to, days,
 *                                        revenue, units, returns, previous_revenue, previous_units, previous_returns }]
 *       trend is /api/sales-hub `trend` (one entry per day, with previous_date aligned by offset).
 *       weekly=true sums consecutive 7-day blocks counted from the first day (the last block may be short).
 *   shouldBucketWeekly(days)        -> true when the range is longer than 45 days.
 *   topMovers(rows, n)              -> { gainers, decliners } ranked by revenue_delta (₹), never by %.
 *   bucketLabel(bucket, weekly, withYear) -> "28 Oct" / "7–13 Oct" for axes and tooltips ("28 Oct 2026" with withYear).
 *   previousBucketLabel(bucket, weekly)   -> the comparison bucket's dates, e.g. "28 Oct 2025".
 *   bucketYearsDiffer(bucket)       -> true when the bucket and its comparison fall in different years
 *                                       (e.g. compare=last_year), so labels need the year to be told apart.
 *   dailyRecordsQuery(scope, from, to) -> query string for /daily-sales with the page filters.
 */
import { formatDateRange } from '../format';

export const WEEKLY_AFTER_DAYS = 45;
export const TREND_METRICS = ['revenue', 'units', 'returns'];

export const shouldBucketWeekly = days => Number(days) > WEEKLY_AFTER_DAYS;

export const bucketTrend = (trend = [], { weekly = false } = {}) => {
  const list = Array.isArray(trend) ? trend : [];
  const size = weekly ? 7 : 1;
  const out = [];
  list.forEach((day, index) => {
    if (index % size === 0) {
      out.push({
        key: day.date, from: day.date, to: day.date,
        previous_from: day.previous_date || null, previous_to: day.previous_date || null, days: 0,
        revenue: 0, units: 0, returns: 0, previous_revenue: 0, previous_units: 0, previous_returns: 0,
      });
    }
    const bucket = out[out.length - 1];
    bucket.to = day.date;
    if (day.previous_date) bucket.previous_to = day.previous_date;
    bucket.days += 1;
    TREND_METRICS.forEach(metric => {
      bucket[metric] += Number(day[metric]) || 0;
      bucket[`previous_${metric}`] += Number(day[`previous_${metric}`]) || 0;
    });
  });
  out.forEach(bucket => {
    bucket.revenue = Math.round(bucket.revenue * 100) / 100;
    bucket.previous_revenue = Math.round(bucket.previous_revenue * 100) / 100;
  });
  return out;
};

export const bucketYearsDiffer = bucket => Boolean(bucket?.from && bucket?.previous_from)
  && (String(bucket.from).slice(0, 4) !== String(bucket.previous_from).slice(0, 4)
    || String(bucket.to).slice(0, 4) !== String(bucket.previous_to || bucket.previous_from).slice(0, 4));

export const bucketLabel = (bucket, weekly = false, withYear = false) => {
  if (!bucket) return '';
  return weekly ? formatDateRange(bucket.from, bucket.to, withYear) : formatDateRange(bucket.from, bucket.from, withYear);
};

export const previousBucketLabel = (bucket, weekly = false) => {
  if (!bucket?.previous_from) return '';
  const to = weekly ? (bucket.previous_to || bucket.previous_from) : bucket.previous_from;
  return formatDateRange(bucket.previous_from, to, bucketYearsDiffer(bucket));
};

export const topMovers = (rows = [], n = 5) => {
  const list = (rows || []).filter(row => row && Number.isFinite(Number(row.revenue_delta)));
  const gainers = list.filter(row => row.revenue_delta > 0)
    .sort((a, b) => b.revenue_delta - a.revenue_delta || String(a.key).localeCompare(String(b.key))).slice(0, n);
  const decliners = list.filter(row => row.revenue_delta < 0)
    .sort((a, b) => a.revenue_delta - b.revenue_delta || String(a.key).localeCompare(String(b.key))).slice(0, n);
  return { gainers, decliners };
};

/** /daily-sales link params for a date range plus the page's platform / brand / category / product filters. */
export const dailyRecordsQuery = (scope = {}, from, to) => {
  const params = new URLSearchParams();
  params.set('date_from', from);
  params.set('date_to', to);
  ['platform', 'brand', 'category'].forEach(key => {
    if (scope[key] && scope[key] !== 'ALL') params.set(key, scope[key]);
  });
  // sku is an exact SKU filter on Sales records; search stays free text.
  if (scope.sku) params.set('sku', scope.sku);
  if (scope.search) params.set('search', scope.search);
  return params.toString();
};
